import React, { lazy, Suspense, useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import candidateSubmissionConfig from "../../utilities/candidateSubmissionConfig";

const ReactQuill = lazy(() => import("react-quill"));

const RequiredFieldRenderer = ({
  field,
  selectedCandidate,
  handleFieldChange,
  disabledFields,
  locationList,
  locationLoading,
  locationChangeHandler,
  locationSearchText,
  locationTextFieldChangeHandler,
  preferredLocationList,
  preferredLocationLoading,
  preferredLocationChangeHandler,
  preferredLocationSearchText,
  preferredLocationTextFieldChangeHandler,
  clientsWorkedWithOptions,
  currentClientsWorkedWithValue,
  handleClientsWorkedWithChange,
  clientsWorkedWithTextFieldHandler,
  currencyOptions,
  setSubmitDataFromModal,
  setSelectedCandidate,
  fieldAltNames,
  allowedSubmissionCodes
}) => {
  const currencyOptionsData = [...new Set(currencyOptions)];
  const unitOptions = ["per hour", "per day", "per month", "per year"];

  // track whether react-quill CSS has been loaded (used by Suspense fallback logic)
  const [quillCssLoaded, setQuillCssLoaded] = useState(false);

  useEffect(() => {
    // only attempt to load quill CSS when the synopsis field is being rendered
    if (field?.submissionCode === candidateSubmissionConfig.candidateSynopsis && !quillCssLoaded) {
      import("react-quill/dist/quill.snow.css")
        .then(() => setQuillCssLoaded(true))
        .catch(() => {
          // keep false on failure so the fallback UI remains visible
          setQuillCssLoaded(false);
        });
    }
  }, [field?.submissionCode, quillCssLoaded]);

  function calculateRates({ amount, unit }) {
    if (!amount || !unit) {
      return { hourly: '', daily: '', monthly: '', yearly: '' };
    }

    const num = parseFloat(amount);
    if (isNaN(num) || num <= 0) {
      return { hourly: '', daily: '', monthly: '', yearly: '' };
    }

    const HOUR_PER_DAY = 9;
    const DAYS_PER_MONTH = 22;
    const MONTHS_PER_YEAR = 12;

    let hourly = '', daily = '', monthly = '', yearly = '';

    switch (unit) {
      case 'per hour':
        hourly = num;
        daily = +(hourly * HOUR_PER_DAY).toFixed(2);
        monthly = +(daily * DAYS_PER_MONTH).toFixed(2);
        yearly = +(monthly * MONTHS_PER_YEAR).toFixed(2);
        break;

      case 'per day':
        daily = num;
        hourly = +(daily / HOUR_PER_DAY).toFixed(2);
        monthly = +(daily * DAYS_PER_MONTH).toFixed(2);
        yearly = +(monthly * MONTHS_PER_YEAR).toFixed(2);
        break;

      case 'per month':
        monthly = num;
        daily = +(monthly / DAYS_PER_MONTH).toFixed(2);
        hourly = +(daily / HOUR_PER_DAY).toFixed(2);
        yearly = +(monthly * MONTHS_PER_YEAR).toFixed(2);
        break;

      case 'per year':
        yearly = num;
        monthly = +(yearly / MONTHS_PER_YEAR).toFixed(2);
        daily = +(monthly / DAYS_PER_MONTH).toFixed(2);
        hourly = +(daily / HOUR_PER_DAY).toFixed(2);
        break;

      default:
        return { hourly: '', daily: '', monthly: '', yearly: '' };
    }

    return { hourly, daily, monthly, yearly };
  }

  const handleRateChange = (fieldSubCode, value, unit, rawValue, currency) => {
  
  handleFieldChange(fieldSubCode, { ...rawValue, amount: value, unit, currency });

  
  const subCodeMap = {
    current: {
      hourly: candidateSubmissionConfig.currenthourlyrate,
      daily: candidateSubmissionConfig.currentdailyrate,
      monthly: candidateSubmissionConfig.currentmonthlyrate,
      yearly: candidateSubmissionConfig.currentyearlyrate
    },
    expected: {
      hourly: candidateSubmissionConfig.expectedhourlyrate,
      daily: candidateSubmissionConfig.expecteddailyrate,
      monthly: candidateSubmissionConfig.expectedmonthlyrate,
      yearly: candidateSubmissionConfig.expectedyearlyrate
    }
  };

  
  let group = null;
  for (const [groupName, codes] of Object.entries(subCodeMap)) {
    if (Object.values(codes).includes(fieldSubCode)) {
      group = codes;
      break;
    }
  }
  if (!group) return; 

  
  const newRates = calculateRates({ amount: value, unit });

  
  Object.entries(newRates).forEach(([rateUnit, rateValue]) => {
    const subCode = group[rateUnit];
    if (subCode && subCode !== fieldSubCode) { 
      handleFieldChange(subCode, {
        amount: rateValue || '',
        unit: `per ${rateUnit}`,
        currency: rawValue.currency || currency 
      });
    }
  });

  
  const baseRate = unit.replace('per ', '');
  handleFieldChange(fieldSubCode, {
    ...rawValue,
    amount: newRates[baseRate] || value, 
    unit,
    currency: rawValue.currency || currency
  });
};

  function formatLocation(obj) {
    if (!obj || typeof obj !== 'object') return '';
    const { country = '', state = '', city = '' } = obj;
    return [city, state, country].filter(Boolean).join(', ');
  }

  const formatLocationArray = (locationArray) => {
    if (!Array.isArray(locationArray)) return [];
    return locationArray.map(loc => {
      if (typeof loc === 'object' && loc !== null) {
        return formatLocation(loc);
      }
      return loc || "";
    });
  };

  if (!field || !field.submissionCode || !field.dataType) {
    return null;
  }

  if ((field.dataType === "String" || field.dataType === "Number") && field.submissionCode !== candidateSubmissionConfig.candidateSynopsis) {
    const currentValue = selectedCandidate.submitData?.candidateSubmissionData?.details?.find(item => item.subCode === field.submissionCode)?.value || "";

    // Special rendering for experience fields (years + months) - CSF04 (yr_of_exp) and CSF05 (relevantExperience)
    const altName = fieldAltNames[field.submissionCode];
    if ((field.submissionCode === candidateSubmissionConfig.totalExperience || field.submissionCode === candidateSubmissionConfig.relevantExperience) || (altName === 'yr_of_exp')) {
      // Parse currentValue which should now be an object {years, months}
      let years = '';
      let months = '';
      if (currentValue && typeof currentValue === 'object') {
        years = currentValue.years ?? currentValue.year ?? currentValue.y ?? '';
        months = currentValue.months ?? currentValue.month ?? currentValue.m ?? '';
      } else {
        years = '';
        months = '';
      }
      const isError =
        ((years === '' || Number(years) === 0) &&
          (months === '' || Number(months) === 0)) ||
        (months !== '' && Number(months) > 0 && years === '');


      const updateValueFromParts = (y, m) => {
        const experienceObject = {
          years: y === '' ? '' : parseInt(y) || 0,
          months: m === '' ? '' : parseInt(m) || 0
        };
        handleFieldChange(field.submissionCode, experienceObject);
      };

      return (
        <div className="fieldContainer_bulkSubmit" key={field.submissionCode}>
          <label className="fieldLabel_requiredFields">
            {field.name || ""}*
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <TextField
              className="textField_bulkSubmit"
              variant="outlined"
              type="number"
              value={years}
              onChange={(e) => {
                const newYears = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                updateValueFromParts(newYears, months);
              }}
              disabled={disabledFields}
              size="small"
              placeholder="Years"
              label="Years"
              fullWidth
              inputProps={field.dataType === "Number" ? { min: 0 } : {}}
              error={isError && !disabledFields}
            />
            <Autocomplete
              className="autocomplete_bulkSubmit"
              options={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]}
              disabled={disabledFields}
              getOptionLabel={(option) => option !== null && option !== undefined ? option.toString() : ''}
              value={months === '' ? null : Number(months)}
              onChange={(e, newValue) => {
                const newMonths = newValue === null || newValue === undefined ? '' : newValue;
                updateValueFromParts(years, newMonths);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Months"
                  variant="outlined"
                  size="small"
                  label="Months"
                  error={isError && !disabledFields}
                />
              )}
            />
          </div>
        </div>
      );
    }

    if (field.submissionCode === candidateSubmissionConfig.contactNo) {
      // Get the current phone number value
      let currentValue = selectedCandidate.submitData?.candidateSubmissionData?.details
        ?.find(item => item.subCode === field.submissionCode)?.value || "";

      // Get country calling code, prioritizing stored value if it starts with '+'
      const storedCountryCode = currentValue.match(/^\+\d+/)?.[0];
      const countryCallingCode = storedCountryCode ||
        selectedCandidate.submitData?.candidateCompleteResume?.countryCallingCode ||
        selectedCandidate.submitData?.countryCallingCode ||
        "+91";

      // Normalize phone number function
      const normalizePhoneNumber = (phoneValue, countryCode) => {
        if (!phoneValue) return "";

        let cleanValue = phoneValue.toString().replace(/[-\s()[\]]/g, "");

        // If the input starts with a valid country code (+ followed by digits)
        if (cleanValue.startsWith('+')) {
          const numberPart = cleanValue.slice(cleanValue.indexOf('+') + 1);
          // Validate that the number part is numeric and total length is reasonable
          if (!/^\d+$/.test(numberPart) || cleanValue.length > 15) {
            return cleanValue; // Return as-is for user correction
          }
          return cleanValue;
        }

        const countryCodeDigits = countryCode.replace('+', '');

        // If the input starts with the country code digits (e.g., '91' for '+91')
        if (cleanValue.startsWith(countryCodeDigits)) {
          const numberPart = cleanValue.slice(countryCodeDigits.length);
          if (!/^\d+$/.test(numberPart) || (countryCode + numberPart).length > 15) {
            return cleanValue; // Return as-is
          }
          return '+' + cleanValue;
        }

        // Validate that the input is numeric and total length is reasonable
        if (!/^\d+$/.test(cleanValue) || (countryCode + cleanValue).length > 15) {
          return cleanValue; // Return as-is
        }
        return countryCode + cleanValue;
      };

      // Normalize the initial value
      const displayedValue = normalizePhoneNumber(currentValue, countryCallingCode);

      // Update state with normalized value on mount or when currentValue changes
      if (currentValue && currentValue !== displayedValue) {
        setSubmitDataFromModal(prevSubmitData => {
          const submitDataFromModalCopy = [...prevSubmitData];
          const index = submitDataFromModalCopy.findIndex(item => item.email === selectedCandidate.submitData?.email);
          if (index === -1) return prevSubmitData;

          const details = submitDataFromModalCopy[index].candidateSubmissionData?.details || [];
          const fieldIndex = details.findIndex(item => item.subCode === field.submissionCode);

          if (fieldIndex !== -1) {
            details[fieldIndex].value = displayedValue;
          } else {
            details.push({
              subCode: field.submissionCode,
              value: displayedValue
            });
          }

          submitDataFromModalCopy[index].phone = displayedValue;
          submitDataFromModalCopy[index].candidateSubmissionData = {
            ...submitDataFromModalCopy[index].candidateSubmissionData,
            details
          };

          // Update countryCallingCode
          const countryCodeMatch = displayedValue.match(/^\+\d+/)?.[0];
          if (countryCodeMatch) {
            submitDataFromModalCopy[index].candidateCompleteResume = submitDataFromModalCopy[index].candidateCompleteResume || {};
            submitDataFromModalCopy[index].candidateCompleteResume.countryCallingCode = countryCodeMatch;
            submitDataFromModalCopy[index].countryCallingCode = countryCodeMatch;
          }

          setSelectedCandidate(prevCandidate => ({
            ...prevCandidate,
            submitData: { ...submitDataFromModalCopy[index] }
          }));

          return submitDataFromModalCopy;
        });
      }

      // Validate phone number for UI feedback
      const isValidPhone = displayedValue === "" || (displayedValue.match(/^\+\d+$/) && displayedValue.length <= 15);

      return (
        <div className="fieldContainer_bulkSubmit" key={field.submissionCode}>
          <label className="fieldLabel_requiredFields">
            {field.name || ""}*
          </label>
          <TextField
            className="textField_bulkSubmit"
            variant="outlined"
            type="tel"
            value={displayedValue}
            onChange={(e) => {
              const input = e.target.value || "";
              const normalizedPhone = normalizePhoneNumber(input, countryCallingCode);

              setSubmitDataFromModal(prevSubmitData => {
                const submitDataFromModalCopy = [...prevSubmitData];
                const index = submitDataFromModalCopy.findIndex(item => item.email === selectedCandidate.submitData?.email);
                if (index === -1) return prevSubmitData;

                const details = submitDataFromModalCopy[index].candidateSubmissionData?.details || [];
                const fieldIndex = details.findIndex(item => item.subCode === field.submissionCode);

                if (fieldIndex !== -1) {
                  details[fieldIndex].value = normalizedPhone;
                } else {
                  details.push({
                    subCode: field.submissionCode,
                    value: normalizedPhone
                  });
                }

                submitDataFromModalCopy[index].phone = normalizedPhone;
                submitDataFromModalCopy[index].candidateSubmissionData = {
                  ...submitDataFromModalCopy[index].candidateSubmissionData,
                  details
                };

                const countryCodeMatch = normalizedPhone.match(/^\+\d+/)?.[0];
                if (countryCodeMatch) {
                  submitDataFromModalCopy[index].candidateCompleteResume = submitDataFromModalCopy[index].candidateCompleteResume || {};
                  submitDataFromModalCopy[index].candidateCompleteResume.countryCallingCode = countryCodeMatch;
                  submitDataFromModalCopy[index].countryCallingCode = countryCodeMatch;
                }

                setSelectedCandidate(prevCandidate => ({
                  ...prevCandidate,
                  submitData: { ...submitDataFromModalCopy[index] }
                }));

                return submitDataFromModalCopy;
              });
            }}
            error={!isValidPhone}
            helperText={!isValidPhone ? "Please enter a valid phone number (e.g., +919310127964, max 15 digits)" : ""}
            disabled={
              disabledFields ||
              [candidateSubmissionConfig.candidateName, candidateSubmissionConfig.emailID, candidateSubmissionConfig.contactNo].includes(field.submissionCode)
            }
            size="small"
            fullWidth
            placeholder={`Enter ${field.name || ""} (e.g., +919310127964)`}
          />
        </div>
      );
    }

    return (
      <div className="fieldContainer_bulkSubmit" key={field.submissionCode}>
        <label className="fieldLabel_requiredFields">
          {field.name || ""}*{candidateSubmissionConfig.noticePeriod === field.submissionCode ? " (in days)" : ""}
        </label>
        <TextField
          className="textField_bulkSubmit"
          variant="outlined"
          type={field.dataType === "Number" ? "number" : "text"}
          value={currentValue}
          onChange={(e) => handleFieldChange(field.submissionCode, e.target.value)}
          disabled={
            disabledFields ||
            [candidateSubmissionConfig.candidateName, candidateSubmissionConfig.emailID, candidateSubmissionConfig.contactNo].includes(field.submissionCode)
          }
          size="small"
          fullWidth
          placeholder={`Enter ${field.name || ""}`}
          inputProps={field.dataType === "Number" ? { min: 0 } : {}}
        />
      </div>
    );
  }

  if (field.dataType === "String" && field.submissionCode === candidateSubmissionConfig.candidateSynopsis) {
    const rawValue = selectedCandidate.submitData?.candidateSubmissionData?.details?.find(item => item.subCode === field.submissionCode)?.value ?? "";
    let currentValue = rawValue;
    const modules = {
      toolbar: [
        [{ list: "bullet" }],
      ],
    };

    const handleSynopsysChange = (value) => {
      if (/<p>.*?<\/p>/.test(value)) {
        value = value.replace("<p>", "");
        value = value.replace("</p>", "");
        value = `<ul><li>${value}</li></ul>`;
      }
      if (value === "<ul><li><br></li></ul>") {
        value = "";
      }
      handleFieldChange(field.submissionCode, value);
    };

    return (
      <div className="fieldContainer_bulkSubmit mb_30" key={selectedCandidate?.candidateData?.emailID}>
        <label className="fieldLabel_requiredFields">
          {field.name || ""}*
        </label>

        <Suspense fallback={<div style={{ minHeight: 120, display: 'flex', alignItems: 'center' }}><CircularProgress size={20} /></div>}>
          {quillCssLoaded ? (
            <ReactQuill
              value={currentValue}
              onChange={handleSynopsysChange}
              key={selectedCandidate?.candidateData?.emailID}
              modules={modules}
              theme="snow"
              placeholder="Enter Candidate Synopsis"
              readOnly={disabledFields}
            />
          ) : (
            // CSS not yet loaded — show lightweight fallback while module and CSS load
            <div style={{ minHeight: 120, padding: 8, border: "1px solid #e0e0e0", borderRadius: 4, display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} />
              <span style={{ marginLeft: 8 }}>Loading editor…</span>
            </div>
          )}
        </Suspense>
      </div>
    );
  }

  if (field.dataType === "Object" && field.submissionCode === candidateSubmissionConfig.currentLocation) {
    const rawValue = selectedCandidate.submitData?.candidateSubmissionData?.details?.find(item => item.subCode === field.submissionCode)?.value;
    const currentValue = typeof rawValue === 'object' ? formatLocation(rawValue) : (rawValue || "");
    
    return (
      <div className="fieldContainer_bulkSubmit">
        <label className="fieldLabel_requiredFields">
          {field.name || ""}*
        </label>
        <Autocomplete
          freeSolo
          fullWidth
          options={locationList}
          value={currentValue}
          onChange={locationChangeHandler}
          disabled={disabledFields}
          loading={locationLoading}
          filterSelectedOptions
          autoHighlight
          getOptionLabel={(option) =>
            typeof option === 'object' ? formatLocation(option) : (option || "")
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              value={locationSearchText}
              onChange={locationTextFieldChangeHandler}
              placeholder="Search locations"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {locationLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </div>
    );
  }

  if (field.dataType === "Object" && (field.submissionCode === candidateSubmissionConfig.currentCTC || field.submissionCode === candidateSubmissionConfig.expectedCTC)) {
    // Current / Expected CTC
    const rawValue = selectedCandidate.submitData?.candidateSubmissionData?.details?.find(item => item.subCode === field.submissionCode)?.value || {};
    const amount = rawValue?.amount || '';
    const currency = rawValue?.currency || '';
    // Default to "per year" for CTC fields
    const unit = rawValue?.unit || 'per year';

    return (
      <div className="fieldContainer_bulkSubmit" key={field.submissionCode}>
        <label className="fieldLabel_requiredFields">{field.name || ""}*</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Autocomplete
            className="autocomplete_bulkSubmit"
            options={currencyOptionsData}
            getOptionLabel={(option) => option || ''}
            disabled={disabledFields}
            value={currency || null}
            onChange={(e, newValue) => handleFieldChange(field.submissionCode, { ...rawValue, currency: newValue || '' })}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Currency"
                variant="outlined"
                size="small"
              />
            )}
          />
          <TextField
            variant="outlined"
            size="small"
            placeholder="Amount"
            type="number"
            fullWidth
            value={amount}
            onChange={(e) => handleFieldChange(field.submissionCode, { ...rawValue, amount: e.target.value })}
            disabled={disabledFields}
            inputProps={{ min: 0 }} 
          />
          <Autocomplete
            className="autocomplete_bulkSubmit"
            freeSolo
            disableClearable
            fullWidth
            options={unitOptions}
            disabled={disabledFields}
            getOptionLabel={(option) => option || ''}
            value={unit || null}
            onChange={(e, newValue) => handleFieldChange(field.submissionCode, { ...rawValue, unit: newValue || '' })}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Unit"
                variant="outlined"
                size="small"
              />
            )}
          />
        </div>
      </div>
    );
  }

   if (field.dataType === "Object" && allowedSubmissionCodes.includes(field.submissionCode)) {
  const rawValue = selectedCandidate.submitData?.candidateSubmissionData?.details?.find(item => item.subCode === field.submissionCode)?.value || {};
  const amount = rawValue?.amount || '';
  const currency = rawValue?.currency || '';

  let unit = rawValue?.unit || '';
  if (!unit) {
    const fieldSubCode = field.submissionCode;
    if ([candidateSubmissionConfig.currenthourlyrate, candidateSubmissionConfig.expectedhourlyrate].includes(fieldSubCode)) {
      unit = 'per hour';
    } else if ([candidateSubmissionConfig.currentdailyrate, candidateSubmissionConfig.expecteddailyrate].includes(fieldSubCode)) {
      unit = 'per day';
    } else if ([candidateSubmissionConfig.currentmonthlyrate, candidateSubmissionConfig.expectedmonthlyrate].includes(fieldSubCode)) {
      unit = 'per month';
    } else if ([candidateSubmissionConfig.currentyearlyrate, candidateSubmissionConfig.expectedyearlyrate].includes(fieldSubCode)) {
      unit = 'per year';
    }
  }

  return (
    <div className="fieldContainer_bulkSubmit" key={field.submissionCode}>
      <label className="fieldLabel_requiredFields">{field.name || ""}*</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Autocomplete
          className="autocomplete_bulkSubmit"
          fullWidth
          options={currencyOptionsData}
          disabled={disabledFields}
          getOptionLabel={(option) => option || ''}
          value={currency || null}
          onChange={(e, newValue) =>
            handleRateChange(field.submissionCode, amount, unit, rawValue, newValue || '')
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Currency"
              variant="outlined"
              size="small"
              error={!currency && !disabledFields} 
            />
          )}
        />
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Amount"
          type="number"
          value={amount}
          disabled={disabledFields}
          onChange={(e) =>
            handleRateChange(field.submissionCode, e.target.value, unit, rawValue, currency)
          }
          error={!amount && !disabledFields} 
          inputProps={{ min: 0 }} 
        />
        <Autocomplete
          className="autocomplete_bulkSubmit"
          freeSolo
          disableClearable
          options={unitOptions}
          fullWidth
          disabled={disabledFields || true} 
          getOptionLabel={(option) => option || ''}
          value={unit || null}
          onChange={(e, newValue) =>
            handleRateChange(field.submissionCode, amount, newValue || unit, rawValue, currency)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Unit"
              variant="outlined"
              size="small"
              error={!unit && !disabledFields} 
            />
          )}
        />
      </div>
    </div>
  );
}


  if (field.dataType === "Array" && field.submissionCode === candidateSubmissionConfig.preferredLocation) {
    // Preferred locations
    const currentValue = selectedCandidate.submitData?.candidateSubmissionData?.details?.find(item => item.subCode === field.submissionCode)?.value || [];
    
    return (
      <div className="fieldContainer_bulkSubmit">
        <label className="fieldLabel_requiredFields">
          {field.name || ""}*
        </label>
        <Autocomplete
          multiple
          freeSolo
          fullWidth
          options={preferredLocationList}
          value={formatLocationArray(currentValue)}
          onChange={preferredLocationChangeHandler}
          disabled={disabledFields}
          loading={preferredLocationLoading}
          filterSelectedOptions
          autoHighlight
          getOptionLabel={(option) =>
            typeof option === 'object' ? formatLocation(option) : (option || "")
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              value={preferredLocationSearchText}
              onChange={preferredLocationTextFieldChangeHandler}
              placeholder="Search preferred locations"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {preferredLocationLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </div>
    );
  }

  if (field.dataType === "Array" && field.submissionCode === candidateSubmissionConfig.clientsWorkedWith) {
    // Clients worked with
    const currentValue = selectedCandidate.submitData?.candidateSubmissionData?.details?.find(item => item.subCode === field.submissionCode)?.value || [];
    
    return (
      <div className="fieldContainer_bulkSubmit">
        <label className="fieldLabel_requiredFields">
          {field.name || ""}*
        </label>
        <Autocomplete
          multiple
          freeSolo
          fullWidth
          options={clientsWorkedWithOptions}
          value={currentValue}
          onChange={handleClientsWorkedWithChange}
          disabled={disabledFields}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              value={currentClientsWorkedWithValue}
              onChange={clientsWorkedWithTextFieldHandler}
              placeholder="Enter client names"
            />
          )}
        />
      </div>
    );
  }

  return null;
};

export default RequiredFieldRenderer;
